import * as React from 'react';
import { useUIState } from '@hotjar/webapp/features/Core/hooks/useUIState';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LandingLayout } from '@hotjar/webapp/components/Landing';
import { usePartialCurrentUser } from '@hotjar/user';
import { ENFORCE_MFA_ENABLE_MFA } from '@hotjar/webapp/constants/routes/mfa';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import type { StepProps } from './types';

const Components: Record<'STEP1' | 'STEP2', React.ComponentType<StepProps>> = {
  STEP1: Step1,
  STEP2: Step2,
};

interface VerifyEmailPageProps {
  backUrl: string;
}

export const VerifyEmailPage = ({ backUrl }: VerifyEmailPageProps): JSX.Element | null => {
  const { setIsAppInterfaceVisible } = useUIState();
  const { data, refetch } = usePartialCurrentUser();
  const [pageState, setPageState] = useState<'STEP1' | 'STEP2'>('STEP1');
  const navigate = useNavigate();

  useEffect(() => {
    setIsAppInterfaceVisible(false);
  }, [setIsAppInterfaceVisible]);

  useEffect(() => {
    refetch().then((value) => {
      if (!value.data?.email) {
        navigate('/login');
      }

      if (value.data?.isEmailVerified) {
        navigate(ENFORCE_MFA_ENABLE_MFA.path);
      }
    });
  }, [navigate, refetch]);

  const onBack = () => {
    switch (pageState) {
      case 'STEP1':
        // Go back
        navigate(backUrl);
        break;
      case 'STEP2':
      default:
        setPageState('STEP1');
        break;
    }
  };

  const onNext = useCallback(() => {
    switch (pageState) {
      case 'STEP1':
        setPageState('STEP2');
        break;
      case 'STEP2':
      default:
        navigate(ENFORCE_MFA_ENABLE_MFA.path);
        break;
    }
  }, [navigate, pageState]);

  const StepComponent = Components[pageState];

  if (StepComponent && data?.email) {
    return (
      <LandingLayout isLoading={false}>
        <StepComponent onBack={onBack} onNext={onNext} email={data.email} />
      </LandingLayout>
    );
  }

  return null;
};
